import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../seo';
import { seoConfigs } from '../../utils/seo-configs';

function InsurancePage() {
  const insuranceProviders = [
    { name: "Blue Cross Blue Shield" },
    { name: "Aetna" },
    { name: "Cigna" },
    { name: "UnitedHealth" },
    { name: "Humana" },
    { name: "Kaiser Permanente" }
  ];

  const steps = [
    {
      id: 1,
      title: "Check Your Benefits",
      description: "Review your mental health coverage details",
      details: [
        "Call the customer service number on your insurance card",
        "Ask about mental health outpatient benefits",
        "Confirm your copay, deductible, and coverage limits",
        "Get a list of in-network mental health providers"
      ]
    },
    {
      id: 2,
      title: "Find In-Network Therapists",
      description: "Use our filters to find therapists who accept your insurance",
      details: [
        "Use our insurance filter when searching",
        "Look for therapists with your specific plan",
        "Verify coverage directly with the therapist's office",
        "Ask about sliding scale options if needed"
      ]
    },
    {
      id: 3,
      title: "Schedule & Verify",
      description: "Book your appointment and confirm coverage",
      details: [
        "Schedule your initial appointment",
        "Bring your insurance card to the first session",
        "Verify your copay amount before the session",
        "Keep records of all sessions for your files"
      ]
    }
  ];

  return (
    <>
      <SEOHead {...seoConfigs.insurance} />
      
      <div className="min-h-screen bg-[#e4fde1]" style={{ marginTop: '-200px', paddingTop: '200px' }}>
        {/* Hero Section */}
        <div className="bg-[#e4fde1] py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-[#254e70]" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
              Insurance Coverage for Therapy
            </h1>
            <p className="text-xl text-[#254e70]/80 mb-8 max-w-3xl mx-auto">
              Find therapists who accept your insurance and learn how to maximize your mental health benefits.
            </p>
            <Link 
              to="/find-therapists"
              className="inline-flex items-center gap-2 bg-[#254e70] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#9dacff] hover:text-white transition-all duration-200"
            >
              Find In-Network Therapists
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Insurance Providers Section */}
        <div className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                Accepted Insurance Providers
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Many of our therapists accept major insurance plans. Find providers who work with your specific insurance.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {insuranceProviders.map((provider, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-200 text-center">
                  <h3 className="text-lg font-semibold text-[#254e70]" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                    {provider.name}
                  </h3>
                </div>
              ))}
            </div>

            <div className="text-center">
              <p className="text-gray-600 mb-4">
                Don't see your insurance? Many of our therapists work with additional providers.
              </p>
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 border-2 border-[#254e70] text-[#254e70] px-6 py-3 rounded-lg font-semibold hover:bg-[#254e70] hover:text-white transition-all duration-200"
              >
                Contact Us for Help
              </Link>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                Using Your Insurance for Therapy
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Follow these simple steps to use your insurance benefits for mental health services.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <div key={step.id} className="relative">
                  {/* Connection line (hidden on mobile) */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-8 -right-4 w-8 h-0.5 bg-[#9dacff] z-10"></div>
                  )}
                  
                  <div className="bg-[#e4fde1] rounded-xl p-8 h-full">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-[#254e70] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                        {step.id}
                      </div>
                      <h3 className="text-xl font-bold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                        {step.title}
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {step.description}
                      </p>
                    </div>
                    
                    <ul className="space-y-3">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start gap-3 text-sm text-gray-700">
                          <svg className="w-4 h-4 text-[#9dacff] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Insurance Tips Section */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                Maximize Your Insurance Benefits
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-8 shadow-md">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                  Understanding Your Benefits
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span><strong>Deductible:</strong> Amount you pay before insurance coverage begins</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span><strong>Copay:</strong> Fixed amount you pay per session</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span><strong>Coinsurance:</strong> Percentage you pay after meeting deductible</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span><strong>Session Limits:</strong> Maximum covered sessions per year</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-8 shadow-md">
                <h3 className="text-xl font-semibold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                  Out-of-Network Options
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>Request reimbursement from your insurance company</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>Use Health Savings Account (HSA) or Flexible Spending Account (FSA)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>Ask therapists about sliding scale pricing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#9dacff] mt-1">•</span>
                    <span>Consider Employee Assistance Programs (EAP)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
                Insurance FAQ
              </h2>
            </div>

            <div className="space-y-6">
              <div className="bg-[#e4fde1] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-[#254e70] mb-2">
                  How do I know if a therapist accepts my insurance?
                </h3>
                <p className="text-gray-700">
                  Use our insurance filter when searching for therapists. Always verify coverage directly with the therapist's office before your first session to confirm they're in-network with your specific plan.
                </p>
              </div>

              <div className="bg-[#e4fde1] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-[#254e70] mb-2">
                  What if I can't find an in-network therapist?
                </h3>
                <p className="text-gray-700">
                  You can see an out-of-network therapist and request reimbursement from your insurance. Many therapists also offer sliding scale fees or payment plans to make therapy more affordable.
                </p>
              </div>

              <div className="bg-[#e4fde1] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-[#254e70] mb-2">
                  Does insurance cover online therapy sessions?
                </h3>
                <p className="text-gray-700">
                  Most insurance plans now cover telehealth therapy sessions at the same rate as in-person visits. Check with your insurance provider to confirm your specific coverage for online therapy.
                </p>
              </div>

              <div className="bg-[#e4fde1] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-[#254e70] mb-2">
                  How many therapy sessions does insurance typically cover?
                </h3>
                <p className="text-gray-700">
                  Coverage varies by plan, but most insurance policies cover 12-26 sessions per year. Some plans may require pre-authorization or have different limits for different types of therapy.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-[#9dacff] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Kanit Medium, sans-serif' }}>
              Ready to Use Your Insurance Benefits?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Find qualified therapists who accept your insurance and start your mental health journey today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/find-therapists"
                className="inline-flex items-center justify-center gap-2 bg-[#254e70] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-[#254e70] transition-all duration-200"
              >
                Search In-Network Therapists
              </Link>
              <Link 
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-[#9dacff] transition-all duration-200"
              >
                Get Insurance Help
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default InsurancePage; 